import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef
} from 'react';
import DeleteModal from '@nile/projects/src/Components/Modals/DeleteModal';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { constructTaskObject, renderTime } from '@nile/projects/src/Utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import {
  AccountClient,
  GET_ALL_TECHS_NEW,
  TicketClient,
  UPDATE_TICKET,
  GET_TASK_LIST,
  UPDATE_TASK,
  DELETE_TASK,
  GET_ALL_TECH_GROUP,
  savePersonalization
} from '@nile/graphql';
import { Warning } from '@styled-icons/material/Warning';
import { MoreVert } from '@styled-icons/material';
import { useQuery } from '@apollo/client';
import {
  AppContext,
  KanbanLoading,
  toast,
  BackgroundIcon,
  EventEmitter,
  AccessControl,
  NewTask,
  Icon,
  Tag,
  getTranslatedText,
  rearrangeArrayValue
} from '@nile/components';
import { cloneDeep } from 'lodash';
import envConfig from '@nile/home/src/Config/env';
import {
  Avatar,
  ButtonGroup,
  Button,
  Card,
  Link
} from '@superops/ui-design-system';
import { PROJECT_STATUSES } from '@nile/projects/src/Components/Cards/ProjectListItem';
import { SuperBoard } from '@nile/components/src/react-super-kanban';
import { Tooltip } from 'react-clone-tippy';
import dompurify from 'dompurify';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  TitleHolder,
  KanbanWrapper
} from '@nile/mangifera/src/StyledComponents/KanbanBoardStyles';
import Queries from '@nile/projects/src/Queries';
import { SVGIcon } from '@superops/super-icons';
import { dompurifier } from '@nile/components/src/Utils/helpers';

const ProjectKanbanWrapper = styled(KanbanWrapper)`
  .kanban-card {
    width: calc(100% - 20px);
    .Superops-Card {
      margin: 0 !important;
      margin-bottom: 16px !important;
      background: var(--sops-white-to-grey-bg);
      border: 1px solid var(--sops-home-widget-border);
      box-sizing: border-box;
      border-radius: 8px;
      box-shadow: none !important;
    }
    .Superops-Button--plain {
      padding: 0 !important;
      margin: 0 !important;
    }
    .options {
      .Superops-Button--plain {
        color: black;
        svg {
          fill: black;
        }
      }
    }
    .ellipsis {
      &,
      .Superops-Button__Text {
        max-width: 150px;
      }
      & > div,
      .Superops-Button__Text > span {
        max-width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        display: block;
      }
    }
  }
`;

const BoardContainer = styled.div`
  height: calc(100% - 45px);
  .kanban-columns-container {
    height: 100%;
  }
`;

const HeaderContainer = styled.div`
  height: 45px;
  display: flex;
  align-items: center;
  padding: 4px;
  border-bottom: 1px solid #ececec;
  background-color: white;
  .Superops-Button--monochrome.Superops-Button--plain .Superops-Button__Text {
    text-decoration: none;
  }
  .Superops-Button {
    height: 3.2rem;
    padding: 0 1.2rem !important;
  }
`;

const StyledText = styled.div`
  text-transform: uppercase;
  margin: 0 30px;
`;

const ROW_COUNT = 20;
const MAX_LANE_COUNT = 7;

function ProjectKanbanContainer(props) {
  const context = useContext(TicketContext);
  const app = useContext(AppContext);
  const currentUser = app?.user || {};
  const user = {
    firstName: currentUser.firstName,
    userId: currentUser.userId,
    email: currentUser.email,
    lastName: currentUser.lastName
  };
  const [displayLaneCount, setDisplayLaneCount] = useState(10);
  const flags = useFlags();
  const { project, setProject, intl, match } = props;
  const [activeTab, setActiveTab] = useState(
    context?.activeTab?.state?.kanbanTab || 'technician'
  );
  const [isLoading, setIsLoading] = useState(true);
  const [kanbanLanes, setKanbanLanes] = useState([]);
  const [otherLanes, setOtherLanes] = useState([]);
  const techRef = useRef(0);
  const milestoneRef = useRef(1);
  const statusRef = useRef(2);
  const contextByRef = useRef(4);
  const activeTabRef = useRef(5);
  activeTabRef.current = activeTab;
  contextByRef.current = context;
  const [technicians, setAllTechnicians] = useState([]);
  techRef.current = technicians;
  const [milestones, setAllMilestones] = useState([]);
  milestoneRef.current = milestones;
  const [statuses, setAllStatus] = useState([]);
  statusRef.current = statuses;
  useEffect(() => {
    setLanes();
  }, [
    project?.projectId,
    project?.totalTasks,
    project?.milestones,
    project?.members
  ]);
  useEffect(() => {
    setLanes();
  }, [activeTab]);
  useEffect(() => {
    setLanes();
  }, [JSON.stringify(context.viewConditions)]);
  useEffect(() => {
    if (!context.viewConditions) {
      context.setViewConditions(context?.activeTab?.state?.condition);
    }
    context.setPersonalization('');
    EventEmitter.subscribe('tabChange', updateTabState);
    return function cleanup() {
      EventEmitter.unsubscribe('tabChange');
    };
  }, []);
  const updateTabState = (eventData) => {
    const currentState = contextByRef.current.activeTab.state || {};
    context.updateTabState(
      {
        ...currentState,
        condition: contextByRef.current.viewConditions,
        kanbanTab: activeTabRef.current
      },
      eventData
    );
  };

  const updateTask = (source, target, item, board, dispatch, updatedBoard) => {
    const { taskId } = item;
    const updateObjectMap = {
      status: { status: target },
      milestone:
        target === 'Other tasks'
          ? { milestone: null }
          : { milestone: { milestoneId: target } },
      technician:
        target === 'Unassigned tasks'
          ? { technician: null }
          : { technician: { userId: target } }
    };
    if (source !== target) {
      TicketClient.mutate({
        mutation: UPDATE_TASK,
        variables: {
          taskId,
          module: 'PROJECT',
          input: updateObjectMap[activeTab]
        }
      })
        .then((res) => {
          if (res?.data?.updateTask) {
            const updateTask = res?.data?.updateTask;
            const newBoardData = cloneDeep(updatedBoard);
            const sourceLaneIdx = newBoardData.findIndex(
              (el) => el.id === source
            );
            const targetLaneIdx = newBoardData.findIndex(
              (el) => el.id === target
            );
            newBoardData[sourceLaneIdx].count -= 1;
            newBoardData[targetLaneIdx].count += 1;
            const itemToUpdate = newBoardData[targetLaneIdx].items.findIndex(
              (el) => el.taskId === updateTask.taskId
            );
            newBoardData[targetLaneIdx].items[itemToUpdate] = {
              ...newBoardData[targetLaneIdx].items[itemToUpdate],
              ...updateTask
            };
            dispatch({ type: 'resetBoard', board: newBoardData });
            toast('success', 'Task updated successfully');
          } else {
            dispatch({ type: 'resetBoard', board: board });
          }
        })
        .catch((err) => {
          dispatch({ type: 'resetBoard', board: board });
        });
    }
  };

  const { loading, data, error } = useQuery(GET_ALL_TECHS_NEW, {
    client: AccountClient,
    variables: {
      input: {
        listInfo: {
          sort: {
            attribute: 'name',
            order: 'ASC'
          }
        }
      }
    },
    onCompleted({ getTechnicianList }) {
      if (getTechnicianList?.userList) {
        const allTechs = getTechnicianList.userList.map((tech) => ({
          ...tech,
          id: tech.userId,
          value: tech.name
        }));
        setAllTechnicians(allTechs);
      }
    }
  });
  const attributeMap = {
    technician: 'technician.userId',
    status: 'status',
    milestone: 'milestone.milestoneId'
  };

  const getOperandsList = (id) => {
    if (id) {
      return [
        {
          attribute: 'project.projectId',
          operator: 'is',
          value: props.project.projectId
        },
        {
          attribute: attributeMap[activeTab],
          operator: 'is',
          value: id
        }
      ];
    }
    return [
      {
        attribute: 'project.projectId',
        operator: 'is',
        value: props.project.projectId
      },
      {
        attribute: attributeMap[activeTab],
        operator: 'isEmpty',
        value: null
      }
    ];
  };

  const fetchTasks = async (id, params = { page: 1, pageSize: ROW_COUNT }) => {
    const res = await TicketClient.query({
      query: GET_TASK_LIST,
      variables: {
        listInfo: {
          page: params.page,
          pageSize: params.pageSize,
          condition: {
            joinOperator: 'AND',
            operands: getOperandsList(id)
          },
          sort: [
            {
              attribute: 'taskOrder',
              order: 'ASC',
              emptyAtLast: true
            }
          ]
        }
      },
      fetchPolicy: 'network-only'
    });
    if (res?.data?.getTasks?.tasks) {
      return {
        cards: res?.data?.getTasks?.tasks.map((task) => ({
          ...task,
          id: task.taskId
        })),
        count: res?.data?.getTasks?.listInfo.totalCount
      };
    }
    return [];
  };

  const handlePagination = async (requestedPage, laneId, board) => {
    const refMap = {
      technician: techRef,
      milestone: milestoneRef,
      status: statusRef
    };
    const keyMap = {
      technician: 'id',
      milestone: 'name',
      status: 'name'
    };
    const currentLaneIndex = board.findIndex((el) => el.id === laneId);
    const currentLane = board[currentLaneIndex];
    const totalCount = currentLane.count;
    if ((requestedPage - 1) * ROW_COUNT < totalCount) {
      const ref = refMap[activeTab];
      const key = keyMap[activeTab];
      const name = ref.current.find((el) => el.id === laneId)?.[key];
      const ticketResponse = await fetchTasks(name, {
        page: requestedPage,
        pageSize: ROW_COUNT
      });
      return ticketResponse.cards;
    }
    return [];
  };

  const reorderLanes = (lanes) => {
    const personalisationName =
      activeTabRef.current === 'status'
        ? activeTabRef.current
        : `${activeTabRef.current}-${project.projectId}`;
    Queries.getColumnOrder(
      personalisationName,
      'ProjectKanbanView',
      currentUser?.userId
    ).then((colData) => {
      if (colData?.length) {
        colData.forEach((colId, i) => {
          const columnIdx = lanes.findIndex((lane) => lane?.id === colId);
          if (columnIdx > -1) {
            rearrangeArrayValue(lanes, columnIdx, i);
          }
        });
      }
      setKanbanLanes(lanes);
      setIsLoading(false);
    });
  };

  const constructTechnicianLanes = async () => {
    setIsLoading(true);
    let allTechs = [];
    allTechs = props.project.members.map((el) => ({
      ...el,
      id: el.user.userId,
      value: el.user.name
    }));
    setAllTechnicians(allTechs);
    const lanes = allTechs.map((el) => {
      return {
        id: el.user.userId,
        title: el.user.name,
        laneId: el.user.userId
      };
    });
    // lanes.unshift({
    //   id: app.user.userId,
    //   title: app.user.name,
    //   laneId: app.user.userId
    // });
    lanes.unshift({
      id: 'Unassigned tasks',
      title: 'Unassigned tasks'
    });
    reorderLanes(lanes);
  };

  const constructMilestoneLanes = async () => {
    setIsLoading(true);
    let allMilestones = [];
    allMilestones = props.project.milestones.map((el) => ({
      ...el,
      id: el.milestoneId,
      value: el.name
    }));
    setAllMilestones(allMilestones);
    const lanes = allMilestones.map((el) => {
      return {
        id: el.milestoneId,
        title: el.name,
        laneId: el.milestoneId
      };
    });
    lanes.unshift({
      id: 'Other tasks',
      title: 'Other tasks'
    });
    reorderLanes(lanes);
  };

  const constructStatusLanes = async () => {
    setIsLoading(true);
    let allStatus = [];
    allStatus = props.statuses.map((el) => ({
      ...el,
      id: el.name,
      value: el.name
    }));
    setAllStatus(allStatus);
    const lanes = allStatus.map((el) => {
      return {
        id: el.id,
        title: el.name,
        laneId: el.name
      };
    });
    reorderLanes(lanes);
  };
  const constructionMap = {
    technician: constructTechnicianLanes,
    milestone: constructMilestoneLanes,
    status: constructStatusLanes
  };

  const setLanes = () => {
    if (props.project.projectId === match.params.id) {
      constructionMap[activeTab]();
    }
  };
  const getListItems = async (params) => {
    const { list } = params;
    const ticketResponse = await fetchTasks(list.laneId);
    return new Promise((resolve) => {
      resolve(
        {
          items: ticketResponse.cards,
          params: { count: ticketResponse.count }
        } || { items: [] }
      );
    });
  };
  const saveBoard = (board) => {
    const personalisedData = {
      user: user,
      name:
        activeTabRef.current === 'status'
          ? activeTabRef.current
          : `${activeTabRef.current}-${project.projectId}`,
      category: `ProjectKanbanView`
    };
    const columnOrderNew = board.map((col) => col.id);
    personalisedData.value = JSON.stringify(columnOrderNew);
    // save the board data in personalisation
    savePersonalization({
      variables: personalisedData,
      input: personalisedData
    });
  };

  const boardChangeHandler = (action, board) => {
    switch (action.type) {
      case 'moveList':
      case 'addList':
      case 'addLists':
      case 'unpinColumn':
      case 'removeFromList':
        saveBoard(board);
        break;
      default:
        break;
    }
  };

  const IntialMap = {
    technician: true
  };

  return (
    <ProjectKanbanWrapper>
      <HeaderContainer data-qa="kanbanViews">
        <StyledText>
          <FormattedMessage id="project.kanban.view.header" />
        </StyledText>
        <ButtonGroup>
          <Button
            monochrome
            outline={activeTab === 'status'}
            plain={activeTab !== 'status'}
            onClick={() => {
              setActiveTab('status');
              setOtherLanes([]);
            }}
          >
            <FormattedMessage id="project.kanban.navbar.tasks" />
          </Button>
          <Button
            monochrome
            outline={activeTab === 'milestone'}
            plain={activeTab !== 'milestone'}
            onClick={() => {
              setActiveTab('milestone');
              setOtherLanes([]);
            }}
          >
            <FormattedMessage id="project.kanban.navbar.milestone" />
          </Button>
          <Button
            monochrome
            outline={activeTab === 'technician'}
            plain={activeTab !== 'technician'}
            onClick={() => {
              setActiveTab('technician');
              setOtherLanes([]);
            }}
          >
            <div data-qa="kanbanTechnician">
              <FormattedMessage id="project.kanban.navbar.tech" />
            </div>
          </Button>
        </ButtonGroup>
      </HeaderContainer>
      {isLoading ? (
        <KanbanLoading />
      ) : (
        <BoardContainer>
          <SuperBoard
            lists={kanbanLanes}
            getListItems={getListItems}
            displayLaneCount={displayLaneCount}
            onLaneScroll={(requestedPage, laneId, board) => {
              const newCards = handlePagination(requestedPage, laneId, board);
              return new Promise((resolve) => {
                resolve(newCards);
              });
            }}
            reorderColumns
            sideEffects={[boardChangeHandler]}
            onCardDrop={(
              sourceId,
              targetId,
              item,
              board,
              dispatch,
              updatedBoard
            ) => {
              updateTask(
                sourceId,
                targetId,
                item,
                board,
                dispatch,
                updatedBoard
              );
              // dispatch({ type: "resetBoard", board: board });
            }}
            initial={!!IntialMap?.[activeTab]}
            components={{
              Card: (otherProps) => (
                <ProjectKanbanCard
                  context={context}
                  {...otherProps}
                  setLanes={setLanes}
                  intl={intl}
                  project={props?.project}
                />
              ),
              ListHeader: (otherProps) => (
                <LaneHeader
                  {...otherProps}
                  setLanes={setLanes}
                  activeTab={activeTab}
                />
              )
            }}
          />
        </BoardContainer>
      )}
    </ProjectKanbanWrapper>
  );
}

function LaneHeader(props) {
  const { name, list, activeTab } = props;
  const { id, title, count } = list;
  const isTechnician = activeTab === 'technician';
  const isUnassigned = id === 'Unassigned tasks';

  return (
    <TitleHolder>
      <div className="flex center title full-width">
        {isTechnician &&
          (!isUnassigned ? (
            <Avatar name={title} initials={title[0]} size="extraSmall" />
          ) : (
            <BackgroundIcon
              url={`url(${envConfig.imageRoot}svg/unassigned.svg)`}
              height="25px"
              width="25px"
              className="mw-25"
            />
          ))}
        <div className="ellipsis ml-10">
          {isUnassigned ? (
            <FormattedMessage id={title} />
          ) : (
            <span className={!isTechnician && 'uppercase'}>{title}</span>
          )}
          {/* {title} */}
        </div>
        <div className="mlauto count">{`${count || '0'}`}</div>
      </div>
    </TitleHolder>
  );
}

function ProjectKanbanCard(props) {
  const { context, item, setLanes, intl } = props;
  const { title, status, technician, estimatedTime } = item;
  const projectContext = useContext(ProjectContext);
  const [sheetActive, setSheetActive] = useState(false);

  const [popoverActive, setPopoverActive] = useState(false);

  const [active, setActive] = useState(false);

  const handleChange = useCallback(() => setActive(!active), [active]);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((val) => !val),
    []
  );

  const delModal = React.createRef();

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };
  const colors = {};

  const updateTaskHandler = (type, task) => {
    const taskObj = constructTaskObject(task);
    TicketClient.mutate({
      mutation: UPDATE_TASK,
      variables: { taskId: item.taskId, module: 'PROJECT', input: taskObj }
    })
      .then((res) => {
        if (res?.data?.updateTask) {
          setLanes();
          toast('success', 'Task updated successfully');
        }
      })
      .finally(() => toggleSheetActive());
  };

  const deleteTaskHandler = () => {
    TicketClient.mutate({
      mutation: DELETE_TASK,
      variables: { taskId: item.taskId, module: 'PROJECT' }
    }).then((res) => {
      if (res?.data?.deleteTask) {
        setLanes();
        toast(
          'success',
          getTranslatedText(intl, 'ticket.details.task.delete.success')
        );
      }
    });
  };

  return (
    <div className="kanban-card">
      <Card padding="sizeSmall">
        <div className="flex actr">
          <Tooltip
            title={dompurifier(title)}
            delay={300}
            duration={250}
            position="bottom"
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
          >
            <Link removeUnderline>
              <div className="ellipsis">
                <AccessControl
                  reference={projectContext.features}
                  roleToCheck="task.edit"
                  render={
                    <NewTask
                      addOrUpdateTechGroup={updateTaskHandler}
                      state={sheetActive}
                      toggleSheetActive={toggleSheetActive}
                      name={title}
                      hideIcon
                      isLink
                      data={item}
                      module="PROJECT_TASK"
                      details={
                        item?.taskId
                          ? {
                              operation: 'UPDATE',
                              projectId: props?.project?.projectId,
                              taskId: item?.taskId
                            }
                          : {
                              operation: 'CREATE',
                              projectId: props?.project?.projectId
                            }
                      }
                      onDeleteHandler={deleteTaskHandler}
                    />
                  }
                  fallback={<div>{title}</div>}
                />
              </div>
            </Link>
          </Tooltip>
          <div className="uppercase mlauto">
            <Tag
              active={PROJECT_STATUSES.ACTIVE.includes(
                status || getTranslatedText(intl, 'project.task.status.planned')
              )}
              smooth={PROJECT_STATUSES.NORMAL.includes(
                status || getTranslatedText(intl, 'project.task.status.planned')
              )}
            >
              {status}
            </Tag>
          </div>
        </div>
        <FieldInfo
          icon="EstimatedTime"
          value={
            estimatedTime > 0 ? (
              renderTime(estimatedTime) || <div className="disabled">NA</div>
            ) : (
              <div className="disabled">NA</div>
            )
          }
        />
        <FieldInfo value={technician?.name} />
      </Card>
      <DeleteModal
        description="Are you sure you want to delete this task"
        handleDeleteConfirmation={deleteTaskHandler}
        title="Delete task"
        reference={delModal}
        active={active}
        handleChange={handleChange}
      />
    </div>
  );
}

function FieldInfo(props) {
  const { icon } = props;
  return (
    <FieldInfoWrapper>
      <div className="flex center info">
        {!icon && <Avatar size="small" type="customer" />}
        {icon && <SVGIcon name={icon} size="sm" />}
        <Value>{props.value || '--'}</Value>
      </div>
    </FieldInfoWrapper>
  );
}

const FieldInfoWrapper = styled.div`
  min-width: 200px;
  margin-top: 10px;
  .info {
    margin-top: 8px;
  }
`;

const Value = styled.div`
  line-height: 16px;
  margin-left: 8px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export default withRouter(injectIntl(ProjectKanbanContainer));
